// Identify a single-segment CTE name at a Monaco cursor and return the
// `CteBinding` that declares it (e.g. cursor on `recent_orders` in
// `SELECT * FROM recent_orders` finds the WITH-clause that defines it).
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 8 (CTE).
//
// Multi-part chains (`schema.tbl`, `cte.col`) belong to column / table
// dispatch — CTE dispatch only fires when the cursor sits on a bare 1-part
// identifier. Recursive self-reference is handled naturally: the CTE
// binding emitted by the resolver names itself, so cursor on the inner
// `cte` inside its own anchor body still resolves to the same binding and
// navigates to `bodyRange`. Multi-CTE with cross-references works the same
// way — every top-level CTE for the cursor's enclosing statement is
// returned by `computeCteBindingsForRange` in declaration order.
//
// Returns `{ binding }` where `binding.target.bodyRange` is the AS-body
// region the dispatcher hands to Monaco as the navigation target.

import type { ParseOptions, SqlEngine } from '../../sql-grammar/model';
import {
  findDottedChainStart,
  parseDottedChain,
} from '../../sql-resolver/dotted/dottedParts';
import { buildParsedDocumentFromSource } from '../../sql-grammar/document';
import { computeCteBindingsForRange } from '../../sql-resolver/scope/cteScope';
import { foldIdentifier } from '../../sql-resolver/scope/identifiers';
import type { CteBinding, ParsedBuffer } from '../../sql-resolver/types';
import type { DatabaseEngineId } from '@/core/engines';
import type { IdentifierBinding } from '../types';
import { databaseEngineIdToSqlEngine } from '../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import { extendCursorToIdentEnd } from './cursorIdent';

export type CteBindingHit = {
  readonly binding: Extract<IdentifierBinding, { kind: 'cte' }>;
};

export type FindCteDeps = {
  readonly engineId: DatabaseEngineId;
};

export function findCteBindingAtCursor(
  source: string,
  offset: number,
  deps: FindCteDeps,
): CteBindingHit | null {
  const sqlEngine = databaseEngineIdToSqlEngine(deps.engineId) ?? 'mssql';
  const options: ParseOptions = {
    engine: sqlEngine,
    identifierFolding: defaultFoldingForEngine(sqlEngine),
  };

  const cursorAtEnd = extendCursorToIdentEnd(source, offset);
  const chainStart = findDottedChainStart(source, cursorAtEnd, options);
  const chain = parseDottedChain(source, chainStart, cursorAtEnd, options);
  if (!chain || chain.parts.length !== 1) return null;
  const ctePart = chain.parts[0];

  const bindings = computeBindings(source, offset, options);
  if (!bindings || bindings.length === 0) return null;

  const folded = foldIdentifier(ctePart.value, ctePart.quoted, options);
  const match = bindings.find((b) => b.name.foldedName === folded);
  if (!match) return null;

  return {
    binding: { kind: 'cte', target: match, definedAt: match.bodyRange },
  };
}

function computeBindings(
  source: string,
  cursorOffset: number,
  options: ParseOptions,
): readonly CteBinding[] | null {
  let parsedDocument;
  try {
    parsedDocument = buildParsedDocumentFromSource(source, options);
  } catch {
    return null;
  }
  const stmt = parsedDocument.statements.find(
    (s) => cursorOffset >= s.range.startOffset && cursorOffset <= s.range.endOffset,
  );
  const start = stmt?.range.startOffset ?? 0;
  const end = stmt?.range.endOffset ?? source.length;
  const buffer = { parsedDocument } as unknown as ParsedBuffer;
  try {
    return computeCteBindingsForRange(buffer, start, end);
  } catch {
    return null;
  }
}

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}
